/**
 * Llamadas HTTP del dominio de autenticación.
 *
 * SIEMPRE sobre el cliente único `lib/api` (`docs/frontend_architecture.md`,
 * Pr. 2): ningún `fetch`/`ky` suelto. El cliente ya aporta `credentials:
 * 'include'` (las cookies httpOnly viajan solas), el `X-CSRF-TOKEN` en las
 * mutaciones y el `X-Sede-Id` (sede activa) en cada request.
 */
import { api } from "@/lib/api";

import type { LoginResponse, MeResponse } from "./types";
import type { LoginValues } from "./schemas";

/**
 * Autentica con usuario/contraseña. En éxito, el backend emite las cookies
 * httpOnly (la respuesta solo trae `{user}`, sin tokens; ver `auth.py` tras la
 * #41). Lanza `ApiError` si las credenciales son inválidas (401).
 */
export async function login(values: LoginValues): Promise<LoginResponse> {
  const response = await api.post("auth/login", { json: values });
  return (await response.json()) as LoginResponse;
}

/**
 * Hidrata la identidad: usuario, permisos y sede activa resuelta del request.
 * Lanza `ApiError` con `status === 401` si no hay sesión (la guarda de auth lo
 * usa para redirigir a login).
 */
export async function fetchMe(): Promise<MeResponse> {
  const response = await api.get("auth/me");
  return (await response.json()) as MeResponse;
}

/**
 * Cierra la sesión: el backend limpia las cookies de JWT (`unset_jwt_cookies`).
 * El cliente no puede borrar la cookie httpOnly desde JS, por eso lo hace el
 * backend; el estado del cliente se limpia aparte (ver `AuthProvider`).
 */
export async function logout(): Promise<void> {
  await api.post("auth/logout");
}
